import React, { useState, useContext, useEffect, memo, useRef } from "react";
import { AuthContext } from "../../context/AuthContext";
import { ProfileContext } from "../../context/ProfileContext";
import { WerkerJobContext } from "../../context/JobContextWerker";
import { SchedJobContext } from "../../context/JobContextScheduler";
import { LoadingScreen } from "../../components/LoadingScreen";
import {
  IonLabel,
  IonSegment,
  IonSegmentButton,
  IonIcon,
  IonBadge,
  useIonAlert,
  IonSearchbar,
  IonButton,
  IonFab,
  IonFabButton,
  IonFabList,
  IonSelect,
  IonSelectOption,
  IonChip,
  IonPopover,
  IonList,
  IonItem,
  IonCheckbox,
  useIonToast,
} from "@ionic/react";
import { Redirect, useLocation } from "react-router-dom";
import {
  PUSH_NOTIFICATION_TYPES,
  WORKFLOW_WERKER_TYPES,
  WERK_SEGMENT_SELECTOR,
} from "../../functions/postActionTypes";
import "../../theme/AppStyles.css";
import {
  add,
  addOutline,
  filterOutline,
  megaphoneOutline,
  swapVerticalOutline,
} from "ionicons/icons";
import { db } from "../../firebase";
import { query, where, collectionGroup, getDocs, updateDoc } from "@firebase/firestore";
import { JobCardsWerker } from "../../components/JobCardsWerker";
import { JobCardsScheduler } from "../../components/JobCardsScheduler";
import { PastJobFilters, DEFAULT_PAST_FILTERS } from "../../utils/jobCardFilters";
import { fx_FB_SendNotification } from "../../functions/Fx_SendPushNotification";
import { WebLayout } from "../../components/layouts/WebLayout";
import { JobDetailPanel } from "../../components/desktop/JobDetailPanel";
import ScheduleJob from "../ScheduleJob";
import SelectWerkers from "../SelectWerkers";
import SelectWerkersSeries from "../selectWerkersSeries";

type PaymentFilter = "all" | "paid" | "unpaid";
type StatusFilter = "all" | "cancelled" | "not_cancelled";
type SortMode = "smart" | "date_newest" | "date_oldest";

// Segment components (copied from Home.tsx)
type WerkerSegmentProps = {
  colorTheme: string;
  mainSegmentWerker: string;
  onChange: (value: string) => void;
  badgeAvailable: number;
  badgeScheduled: number;
  badgePast: number;
};

const WerkerSegment: React.FC<WerkerSegmentProps> = memo(
  ({ colorTheme, mainSegmentWerker, onChange, badgeAvailable, badgeScheduled, badgePast }) => {
    return (
      <React.Fragment>
        <IonSegment
          className={colorTheme}
          value={mainSegmentWerker}
          onIonChange={(e: any) => onChange(e.detail.value!)}
        >
          <IonSegmentButton
            value={WERK_SEGMENT_SELECTOR.WerkerInvited}
          >
            <IonLabel>
              {badgeAvailable > 0 ? (
                <h2>
                  Available{" "}
                  <IonBadge
                    color="danger"
                  >
                    {badgeAvailable}
                  </IonBadge>
                </h2>
              ) : (
                <h2>Available</h2>
              )}
            </IonLabel>
          </IonSegmentButton>

          <IonSegmentButton
            value={WERK_SEGMENT_SELECTOR.WerkerScheduled}
          >
            <IonLabel>
              {badgeScheduled > 0 ? (
                <h2>
                  Scheduled{" "}
                  <IonBadge
                    color="danger"
                  >
                    {badgeScheduled}
                  </IonBadge>
                </h2>
              ) : (
                <h2>Scheduled</h2>
              )}
            </IonLabel>
          </IonSegmentButton>

          <IonSegmentButton
            value={WERK_SEGMENT_SELECTOR.WerkerPast}
          >
            <IonLabel>
              {badgePast > 0 ? (
                <h2>
                  Past{" "}
                  <IonBadge
                    color="danger"
                  >
                    {badgePast}
                  </IonBadge>
                </h2>
              ) : (
                <h2>Past</h2>
              )}
            </IonLabel>
          </IonSegmentButton>
        </IonSegment>
      </React.Fragment>
    );
  }
);

type SchedulerSegmentProps = {
  colorTheme: string;
  mainSegmentScheduler: string;
  onChange: (value: string) => void;
  badgeOpen: number;
  badgeFilled: number;
  badgePast: number;
};

const SchedulerSegment: React.FC<SchedulerSegmentProps> = memo(
  ({ colorTheme, mainSegmentScheduler, onChange, badgeOpen, badgeFilled, badgePast }) => {
    return (
      <React.Fragment>
        <IonSegment
          className={colorTheme}
          value={mainSegmentScheduler}
          onIonChange={(e: any) => onChange(e.detail.value!)}
        >
          <IonSegmentButton
            value={WERK_SEGMENT_SELECTOR.SchedOpen}
          >
            <IonLabel>
              {badgeOpen > 0 ? (
                <h2>
                  Open{" "}
                  <IonBadge
                    color="danger"
                  >
                    {badgeOpen}
                  </IonBadge>
                </h2>
              ) : (
                <h2>Open</h2>
              )}
            </IonLabel>
          </IonSegmentButton>

          <IonSegmentButton
            value={WERK_SEGMENT_SELECTOR.SchedFilled}
          >
            <IonLabel>
              {badgeFilled > 0 ? (
                <h2>
                  Filled{" "}
                  <IonBadge
                    color="danger"
                  >
                    {badgeFilled}
                  </IonBadge>
                </h2>
              ) : (
                <h2>Filled</h2>
              )}
            </IonLabel>
          </IonSegmentButton>

          <IonSegmentButton
            value={WERK_SEGMENT_SELECTOR.SchedPast}
          >
            <IonLabel>
              {badgePast > 0 ? (
                <h2>
                  Past{" "}
                  <IonBadge
                    color="danger"
                  >
                    {badgePast}
                  </IonBadge>
                </h2>
              ) : (
                <h2>Past</h2>
              )}
            </IonLabel>
          </IonSegmentButton>
        </IonSegment>
      </React.Fragment>
    );
  }
);

type SegmentSelectorProps = {
  isScheduler: boolean;
  werkerProps: WerkerSegmentProps;
  schedulerProps: SchedulerSegmentProps;
};

const SegmentSelector: React.FC<SegmentSelectorProps> = memo(({ isScheduler, werkerProps, schedulerProps }) => {
  if (isScheduler) return <SchedulerSegment {...schedulerProps} />;
  return <WerkerSegment {...werkerProps} />;
});

const HomeDesktop: React.FC = memo(() => {
  const { currentUser, authReady } = useContext(AuthContext);
  const location = useLocation<{ segment?: string; jobId?: string } | undefined>();
  const { profile, colorTheme } = useContext(ProfileContext);

  const [mainSegmentWerker, setMainSegmentWerker] = useState<string>(WERK_SEGMENT_SELECTOR.WerkerInvited);
  const [mainSegmentScheduler, setMainSegmentScheduler] = useState<string>(WERK_SEGMENT_SELECTOR.SchedOpen);

  const {
    badgeCountInvitedSegmentWerkerContext,
    badgeCountScheduledSegmentWerkerContext,
    badgeCountPastSegmentWerkerContext,
  } = useContext(WerkerJobContext);

  const {
    badgeCountOpenSegmentSchedulerContext,
    badgeCountFilledSegmentSchedulerContext,
    badgeCountPastSegmentSchedulerContext,
  } = useContext(SchedJobContext);

  const [present] = useIonAlert();
  const [presentToast] = useIonToast();
  const [searchText, setSearchText] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [selectedJobId, setSelectedJobId] = useState<string | null>(null);
  const [editingJobId, setEditingJobId] = useState<string | null>(null);
  const processedLocationKey = useRef<string | null>(null);

  useEffect(() => {
    const locationKey = location.key ?? "default";
    if (processedLocationKey.current === locationKey) return;
    const state = location.state as { segment?: string; jobId?: string } | undefined;
    if (!state?.jobId) return;
    processedLocationKey.current = locationKey;
    
    const jobIdToSelect = state.jobId; // Capture for setTimeout
    
    // Clear any panel modes when navigating from elsewhere (like Messages)
    setScheduleJobPanelMode(null);
    setSelectWerkersJobId(null);
    setSelectWerkersSeriesState(null);
    
    if (state.segment) {
      // Check if it's a scheduler segment
      const isSchedulerSegment = [WERK_SEGMENT_SELECTOR.SchedOpen, WERK_SEGMENT_SELECTOR.SchedFilled, WERK_SEGMENT_SELECTOR.SchedPast].includes(state.segment);
      const isWerkerSegment = [WERK_SEGMENT_SELECTOR.WerkerInvited, WERK_SEGMENT_SELECTOR.WerkerScheduled, WERK_SEGMENT_SELECTOR.WerkerPast].includes(state.segment);
      
      if (isSchedulerSegment) {
        setMainSegmentScheduler(state.segment);
      } else if (isWerkerSegment) {
        setMainSegmentWerker(state.segment);
      }
    }
    
    // Set selected job ID after a brief delay to ensure segment has updated
    setTimeout(() => {
      setSelectedJobId(jobIdToSelect);
    }, 50);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key, location.state]);
  const [scheduleJobPanelMode, setScheduleJobPanelMode] = useState<"create" | "edit" | "clone" | null>(null);
  const [selectWerkersJobId, setSelectWerkersJobId] = useState<string | null>(null);
  const [selectWerkersSeriesState, setSelectWerkersSeriesState] = useState<{ seriesId: string; jobIds?: string[] } | null>(null);

  const sortSelectRef = useRef<HTMLIonSelectElement | null>(null);

  const openSortPopover = async (ev?: UIEvent) => {
    await sortSelectRef.current?.open(ev);
  };

  const [viewOptions, setViewOptions] = useState<{
    payment: PaymentFilter;
    status: StatusFilter;
    sort: SortMode;
  }>({
    payment: "unpaid",
    status: "not_cancelled",
    sort: "smart",
  });

  const hasActiveSort = viewOptions.sort !== "smart";

  const isScheduler = profile.UserLastRoleScheduler === true;
  const isSchedulerPast = isScheduler && mainSegmentScheduler === WERK_SEGMENT_SELECTOR.SchedPast;
  const isWerker = profile.UserLastRoleScheduler !== true;
  const isWerkerPast = isWerker && mainSegmentWerker === WERK_SEGMENT_SELECTOR.WerkerPast;

  // show chips only on Werker Past (Scheduler Past now uses the filter popover)
  const showPastChips = isWerkerPast;

  const [schedPastFilters, setSchedPastFilters] = useState<PastJobFilters>(DEFAULT_PAST_FILTERS);
  const [draftPastFilters, setDraftPastFilters] = useState<PastJobFilters>(DEFAULT_PAST_FILTERS);
  const [filterPopoverOpen, setFilterPopoverOpen] = useState(false);

  const hasActiveFilter =
    schedPastFilters.werkersPaid !== DEFAULT_PAST_FILTERS.werkersPaid ||
    schedPastFilters.werkersUnpaid !== DEFAULT_PAST_FILTERS.werkersUnpaid ||
    schedPastFilters.jobPaid !== DEFAULT_PAST_FILTERS.jobPaid ||
    schedPastFilters.jobUnpaid !== DEFAULT_PAST_FILTERS.jobUnpaid ||
    schedPastFilters.jobCancelled !== DEFAULT_PAST_FILTERS.jobCancelled;

  const canSort =
    (isScheduler &&
      (mainSegmentScheduler === WERK_SEGMENT_SELECTOR.SchedOpen ||
        mainSegmentScheduler === WERK_SEGMENT_SELECTOR.SchedFilled ||
        mainSegmentScheduler === WERK_SEGMENT_SELECTOR.SchedPast)) ||
    (isWerker &&
      (mainSegmentWerker === WERK_SEGMENT_SELECTOR.WerkerScheduled ||
        mainSegmentWerker === WERK_SEGMENT_SELECTOR.WerkerPast));

  useEffect(() => {
    if (profile.UserId === "") {
      setIsLoading(true);
    } else {
      setIsLoading(false);
    }
  }, [profile]);

  const handleSegmentSelection = async (selection: any) => {
    setMainSegmentWerker(selection);
    setSelectedJobId(null); // Clear selection when switching segments
  };

  const handleSegmentSelectionSched = async (selection: any) => {
    setMainSegmentScheduler(selection);
    setSelectedJobId(null); // Clear selection when switching segments
  };

  const searchTextFunction = (typedValue: string) => {
    setSearchText(typedValue);
  };

  const handleJobSelect = (jobDocId: string, _uniqueJobId: string, _schedUid: string) => {
    setSelectedJobId(jobDocId);
    setScheduleJobPanelMode(null);
    setSelectWerkersJobId(null);
    setSelectWerkersSeriesState(null);
  };

  const handleEditJob = (jobDocId: string) => {
    setEditingJobId(jobDocId);
    setScheduleJobPanelMode("edit");
  };

  const handleCloneJob = (jobDocId: string) => {
    setEditingJobId(jobDocId);
    setScheduleJobPanelMode("clone");
  };

  const handleScheduleJobComplete = (result?: { nextView?: "details" | "selectWerkers" | "selectWerkersSeries"; jobId?: string; seriesId?: string; jobIds?: string[] }) => {
    const wasEditing = scheduleJobPanelMode === "edit";
    setScheduleJobPanelMode(null);

    // When editing (not creating), skip select werkers flow and go directly to job details
    if (wasEditing) {
      if (result?.jobId) {
        setSelectedJobId(result.jobId);
      }
      setSelectWerkersJobId(null);
      setSelectWerkersSeriesState(null);
      return;
    }

    // For new jobs, follow the normal flow
    if (result?.nextView === "selectWerkers" && result.jobId) {
      setSelectedJobId(result.jobId);
      setSelectWerkersJobId(result.jobId);
      setSelectWerkersSeriesState(null);
      return;
    }

    if (result?.nextView === "selectWerkersSeries" && result.seriesId && result.jobIds?.length) {
      setSelectedJobId(result.jobIds[0]);
      setSelectWerkersJobId(null);
      setSelectWerkersSeriesState({ seriesId: result.seriesId, jobIds: result.jobIds });
      return;
    }

    if (result?.jobId) {
      setSelectedJobId(result.jobId);
    }

    setSelectWerkersJobId(null);
    setSelectWerkersSeriesState(null);
  };

  const handleSelectWerkersComplete = (result?: { nextView?: "details"; jobId?: string }) => {
    setSelectWerkersJobId(null);
    if (result?.jobId) {
      setSelectedJobId(result.jobId);
    }
  };

  const handleEditWerkersRequest = (result: { nextView: "selectWerkers" | "selectWerkersSeries"; jobId?: string; seriesId?: string }) => {
    setScheduleJobPanelMode(null);

    if (result.nextView === "selectWerkers" && result.jobId) {
      setSelectedJobId(result.jobId);
      setSelectWerkersSeriesState(null);
      setSelectWerkersJobId(result.jobId);
      return;
    }

    if (result.nextView === "selectWerkersSeries" && result.seriesId) {
      setSelectWerkersJobId(null);
      setSelectWerkersSeriesState({ seriesId: result.seriesId, jobIds: undefined });
    }
  };

  const handleSelectWerkersSeriesComplete = (result?: { nextView?: "details"; jobId?: string }) => {
    setSelectWerkersSeriesState(null);
    if (result?.jobId) {
      setSelectedJobId(result.jobId);
    }
  };

  const handleJobDeleted = () => {
    setSelectedJobId(null);
  };

  const openCreateJobInPanel = () => {
    setEditingJobId(null);
    setSelectedJobId(null);
    setScheduleJobPanelMode("create");
  };

  const handleCreateJob = async () => {
    if (profile.Associates.length === 1 && profile.Associates[0] === "XXX") {
      present({
        header: "You have no associates",
        buttons: [{ text: "Create Job Anyway?", handler: openCreateJobInPanel }, "Cancel"],
      });
    } else {
      present({
        header: "Add new job",
        buttons: ["Cancel", { text: "Ok", handler: openCreateJobInPanel }],
      });
    }
  };

  const handleSchedShiftReminderAllJobs = () => {
    const sendReminderToWerkers = async () => {
      try {
        const uniqueJobIds = new Set<string>();
        const unInviteRef = query(
          collectionGroup(db, "jobsDocs"),
          where("docStamp.userInfoFrom.uid", "==", profile.UserId),
          where("jobInfo.status.jobStatusWerker", "==", WORKFLOW_WERKER_TYPES.Invited)
        );

        const querySnapshot = await getDocs(unInviteRef);

        // Build a map of werkerUID -> first uniqueJobDocId so each werker
        // gets a notification that deep-links to their specific job.
        const werkerJobMap = new Map<string, string>();

        // Update isRead and collect data in one pass
        await Promise.all(
          querySnapshot.docs.map(async (docSnap) => {
            const werkerJobDoc = docSnap.data();
            const werkerUid: string = werkerJobDoc.docStamp?.userInfoTo?.uid ?? "";
            const uniqueJobDocId: string = werkerJobDoc.jobInfo?.core?.uniqueJobId ?? "";

            if (werkerUid && profile.UserId !== werkerUid) {
              // Keep the first job found per werker for the notification payload
              if (!werkerJobMap.has(werkerUid) && uniqueJobDocId) {
                werkerJobMap.set(werkerUid, uniqueJobDocId);
              }
            }

            if (uniqueJobDocId) {
              uniqueJobIds.add(uniqueJobDocId);
            }

            await updateDoc(docSnap.ref, { "jobInfo.status.isRead": false });
          })
        );

        // Send one notification per werker with their specific job ID
        await Promise.all(
          Array.from(werkerJobMap.entries()).map(([werkerUid, uniqueJobDocId]) =>
            fx_FB_SendNotification(profile.UserId, [werkerUid], PUSH_NOTIFICATION_TYPES.WerkerJobReminder, {
              schedFName: profile.FirstName,
              schedUid: profile.UserId,
              jobPay: "",
              uniqueJobDocId: uniqueJobDocId,
            })
          )
        );

        // Show completion toast immediately after operation completes
        console.log("Re-alert complete for", uniqueJobIds.size, "jobs");
        await presentToast({
          message: `Re-alert complete for ${uniqueJobIds.size} job${uniqueJobIds.size !== 1 ? 's' : ''}`,
          duration: 3000,
          position: "top",
          color: "success",
        }).then(() => console.log("Completion toast presented successfully"))
          .catch(err => console.error("Completion toast error:", err));
      } catch (error) {
        console.error("sendReminderToWerkers error:", error);
        // Show error toast
        await presentToast({
          message: "Re-alert failed, please try again",
          duration: 2500,
          position: "top",
          color: "danger",
        }).catch(err => console.error("Error toast error:", err));
      }
    };

    present({
      header: "Re-alert Invited Werkers On All Jobs?",
      buttons: [
        "Cancel",
        {
          text: "Ok",
          handler: () => {
            sendReminderToWerkers();
            return true;
          },
        },
      ],
    });
  };

  if (!authReady) {
    return <LoadingScreen LoadingText="Loading" />;
  }

  if (currentUser.uid === "") {
    return <Redirect exact from="/" to={{ pathname: "/login", search: "?returnUrl=/portal" }} />;
  }

  return (
    <WebLayout title="Werk">
      <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {isLoading === true && <LoadingScreen LoadingText={"Loading"} />}

      {isLoading === false && (
        <div style={{ flex: 1, display: 'flex', flexDirection: 'row', overflow: 'hidden', width: '100%' }}>
            {/* Center Column - Job List */}
            <div 
              style={{
                width: '50%',
                height: "100%",
                padding: "0",
                position: "relative",
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <div
                style={{
                  flexShrink: 0,
                  zIndex: 20,
                  background: "var(--ion-background-color)",
                  borderBottom: "1px solid var(--ion-color-light-shade)",
                }}
              >
                <SegmentSelector
                  isScheduler={profile.UserLastRoleScheduler === true}
                  werkerProps={{
                    colorTheme,
                    mainSegmentWerker,
                    onChange: (v) => handleSegmentSelection(v),
                    badgeAvailable: badgeCountInvitedSegmentWerkerContext,
                    badgeScheduled: badgeCountScheduledSegmentWerkerContext,
                    badgePast: badgeCountPastSegmentWerkerContext,
                  }}
                  schedulerProps={{
                    colorTheme,
                    mainSegmentScheduler,
                    onChange: (v) => handleSegmentSelectionSched(v),
                    badgeOpen: badgeCountOpenSegmentSchedulerContext,
                    badgeFilled: badgeCountFilledSegmentSchedulerContext,
                    badgePast: badgeCountPastSegmentSchedulerContext,
                  }}
                />

                <IonSearchbar
                  value={searchText}
                  onIonChange={(e) => searchTextFunction(e.detail.value!)}
                  animated
                />

                {/* Searchbar row: filter (sched past only) + sort */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 4,
                    paddingRight: 4,
                    minHeight: 41,
                    boxSizing: "border-box",
                  }}
                >
                  <div style={{ flex: 1 }} />

                  {/* Filter button — Scheduler Past only */}
                  {isSchedulerPast && (
                    <>
                      <IonButton
                        id="sched-past-filter-trigger-desktop"
                        fill="clear"
                        style={{ fontWeight: 600, position: "relative" }}
                        size="small"
                        onClick={() => {
                          setDraftPastFilters(schedPastFilters);
                          setFilterPopoverOpen(true);
                        }}
                      >
                        Filter
                        <IonIcon size="small" icon={filterOutline} />
                        {hasActiveFilter && (
                          <IonBadge
                            color="danger"
                            style={{
                              position: "absolute",
                              top: "4px",
                              right: "6px",
                              borderRadius: "999px",
                              width: "10px",
                              height: "10px",
                              padding: "0",
                              minWidth: "10px",
                            }}
                          >
                            {" "}
                          </IonBadge>
                        )}
                      </IonButton>

                      <IonPopover
                        trigger="sched-past-filter-trigger-desktop"
                        triggerAction="click"
                        isOpen={filterPopoverOpen}
                        onDidDismiss={() => setFilterPopoverOpen(false)}
                        style={{ "--min-width": "290px" }}
                      >
                        <IonList lines="full" style={{ paddingTop: 8, minWidth: 200 }}>
                          <IonItem>
                            <IonCheckbox
                              slot="start"
                              checked={draftPastFilters.werkersPaid}
                              onIonChange={(e) =>
                                setDraftPastFilters((p) => ({ ...p, werkersPaid: e.detail.checked }))
                              }
                            />
                            <IonLabel style={{ whiteSpace: "nowrap" }}>Werkers Paid</IonLabel>
                          </IonItem>
                          <IonItem>
                            <IonCheckbox
                              slot="start"
                              checked={draftPastFilters.werkersUnpaid}
                              onIonChange={(e) =>
                                setDraftPastFilters((p) => ({ ...p, werkersUnpaid: e.detail.checked }))
                              }
                            />
                            <IonLabel style={{ whiteSpace: "nowrap" }}>Werkers Unpaid</IonLabel>
                          </IonItem>
                          <IonItem>
                            <IonCheckbox
                              slot="start"
                              checked={draftPastFilters.jobPaid}
                              onIonChange={(e) =>
                                setDraftPastFilters((p) => ({ ...p, jobPaid: e.detail.checked }))
                              }
                            />
                            <IonLabel style={{ whiteSpace: "nowrap" }}>Job Paid</IonLabel>
                          </IonItem>
                          <IonItem>
                            <IonCheckbox
                              slot="start"
                              checked={draftPastFilters.jobUnpaid}
                              onIonChange={(e) =>
                                setDraftPastFilters((p) => ({ ...p, jobUnpaid: e.detail.checked }))
                              }
                            />
                            <IonLabel style={{ whiteSpace: "nowrap" }}>Job Unpaid</IonLabel>
                          </IonItem>
                          <IonItem>
                            <IonCheckbox
                              slot="start"
                              checked={draftPastFilters.jobCancelled}
                              onIonChange={(e) =>
                                setDraftPastFilters((p) => ({ ...p, jobCancelled: e.detail.checked }))
                              }
                            />
                            <IonLabel style={{ whiteSpace: "nowrap" }}>Job Cancelled</IonLabel>
                          </IonItem>
                        </IonList>
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "flex-end",
                            gap: 8,
                            padding: "8px 12px",
                          }}
                        >
                          <IonButton
                            fill="clear"
                            size="small"
                            color="medium"
                            onClick={() => setDraftPastFilters(DEFAULT_PAST_FILTERS)}
                          >
                            Default
                          </IonButton>
                          <IonButton
                            fill="clear"
                            size="small"
                            color="medium"
                            onClick={() => setFilterPopoverOpen(false)}
                          >
                            Cancel
                          </IonButton>
                          <IonButton
                            fill="solid"
                            size="small"
                            color="dark"
                            onClick={() => {
                              setSchedPastFilters(draftPastFilters);
                              setFilterPopoverOpen(false);
                            }}
                          >
                            Apply
                          </IonButton>
                        </div>
                      </IonPopover>
                    </>
                  )}

                  {/* Sort button */}
                  {canSort && (
                    <>
                      <IonButton
                        fill="clear"
                        style={{ fontWeight: 600, position: "relative" }}
                        size="small"
                        onClick={(e) => openSortPopover(e.nativeEvent as UIEvent)}
                      >
                        Sort
                        <IonIcon size="small" icon={swapVerticalOutline} />
                        {hasActiveSort && (
                          <IonBadge
                            color="danger"
                            style={{
                              position: "absolute",
                              top: "4px",
                              right: "6px",
                              borderRadius: "999px",
                              width: "10px",
                              height: "10px",
                              padding: "0",
                              minWidth: "10px",
                            }}
                          >
                            {" "}
                          </IonBadge>
                        )}
                      </IonButton>

                      <IonSelect
                        ref={sortSelectRef}
                        value={viewOptions.sort}
                        interface="popover"
                        onIonChange={(e: any) => setViewOptions((p) => ({ ...p, sort: e.detail.value }))}
                        style={{ position: "absolute", opacity: 0, pointerEvents: "none", width: 1, height: 1 }}
                      >
                        <IonSelectOption value="smart">Default</IonSelectOption>
                        <IonSelectOption value="date_newest">Newest</IonSelectOption>
                        <IonSelectOption value="date_oldest">Oldest</IonSelectOption>
                      </IonSelect>
                    </>
                  )}
                </div>

                {/* Chips row — Werker Past only */}
                {showPastChips && (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      padding: "6px 12px",
                      height: 30,
                      boxSizing: "border-box",
                      marginTop: "0px",
                      marginBottom: "5px",
                      overflowX: "auto",
                      overflowY: "hidden",
                      whiteSpace: "nowrap",
                      WebkitOverflowScrolling: "touch",
                    }}
                  >
                    <IonChip
                      className="homeChip"
                      outline={true}
                      color={
                        viewOptions.payment === "paid" || viewOptions.payment === "all" ? "dark" : "medium"
                      }
                      onClick={() => {
                        setViewOptions((p) => {
                          const paidChecked = p.payment === "paid" || p.payment === "all";
                          const nextPaidChecked = !paidChecked;
                          const unpaidChecked = p.payment === "unpaid" || p.payment === "all";
                          const next =
                            (nextPaidChecked && unpaidChecked) || (!nextPaidChecked && !unpaidChecked)
                              ? "all"
                              : nextPaidChecked
                              ? "paid"
                              : "unpaid";
                          return { ...p, payment: next };
                        });
                      }}
                    >
                      <IonLabel className="homeChipLabel">Paid</IonLabel>
                    </IonChip>

                    <IonChip
                      className="homeChip"
                      outline={true}
                      color={
                        viewOptions.payment === "unpaid" || viewOptions.payment === "all" ? "dark" : "medium"
                      }
                      onClick={() => {
                        setViewOptions((p) => {
                          const unpaidChecked = p.payment === "unpaid" || p.payment === "all";
                          const nextUnpaidChecked = !unpaidChecked;
                          const paidChecked = p.payment === "paid" || p.payment === "all";
                          const next =
                            (paidChecked && nextUnpaidChecked) || (!paidChecked && !nextUnpaidChecked)
                              ? "all"
                              : paidChecked
                              ? "paid"
                              : "unpaid";
                          return { ...p, payment: next };
                        });
                      }}
                    >
                      <IonLabel className="homeChipLabel">Unpaid</IonLabel>
                    </IonChip>

                    <IonChip
                      className="homeChip"
                      outline={true}
                      color={
                        viewOptions.status === "not_cancelled" || viewOptions.status === "all"
                          ? "dark"
                          : "medium"
                      }
                      onClick={() => {
                        setViewOptions((p) => {
                          const activeChecked = p.status === "not_cancelled" || p.status === "all";
                          const nextActiveChecked = !activeChecked;
                          const cancelledChecked = p.status === "cancelled" || p.status === "all";
                          const next =
                            (nextActiveChecked && cancelledChecked) || (!nextActiveChecked && !cancelledChecked)
                              ? "all"
                              : nextActiveChecked
                              ? "not_cancelled"
                              : "cancelled";
                          return { ...p, status: next };
                        });
                      }}
                    >
                      <IonLabel className="homeChipLabel">Active</IonLabel>
                    </IonChip>

                    <IonChip
                      className="homeChip"
                      outline={true}
                      color={
                        viewOptions.status === "cancelled" || viewOptions.status === "all"
                          ? "dark"
                          : "medium"
                      }
                      onClick={() => {
                        setViewOptions((p) => {
                          const cancelledChecked = p.status === "cancelled" || p.status === "all";
                          const nextCancelledChecked = !cancelledChecked;
                          const activeChecked = p.status === "not_cancelled" || p.status === "all";
                          const next =
                            (activeChecked && nextCancelledChecked) || (!activeChecked && !nextCancelledChecked)
                              ? "all"
                              : activeChecked
                              ? "not_cancelled"
                              : "cancelled";
                          return { ...p, status: next };
                        });
                      }}
                    >
                      <IonLabel className="homeChipLabel">Cancelled</IonLabel>
                    </IonChip>
                  </div>
                )}
              </div>

              <div
                style={{
                  flex: 1,
                  minHeight: 0,
                  overflowY: "auto",
                  paddingBottom: profile.UserLastRoleScheduler === true ? "96px" : 0,
                }}
              >
                {/* Job Cards */}
                {profile.UserLastRoleScheduler === true ? (
                  <JobCardsScheduler
                    jobCardSearchText={searchText}
                    segmentSelection={mainSegmentScheduler}
                    viewOptions={viewOptions}
                    pastFilters={isSchedulerPast ? schedPastFilters : undefined}
                    onJobSelect={handleJobSelect}
                    selectedJobId={selectedJobId}
                  />
                ) : (
                  <JobCardsWerker
                    jobCardSearchText={searchText}
                    segmentSelection={mainSegmentWerker}
                    PaidStatus={""}
                    viewOptions={viewOptions}
                    onJobSelect={handleJobSelect}
                    selectedJobId={selectedJobId}
                  />
                )}
              </div>

              {profile.UserLastRoleScheduler === true && (
                <IonFab
                  horizontal="end"
                  vertical="bottom"
                  style={{ position: "absolute", right: 20, bottom: 20 }}
                >
                  <IonFabButton
                    color="secondwarning"
                    style={{
                      "--border-radius": "15px",
                      "--box-shadow": "0 10px 26px rgba(0,0,0,0.35), 0 0 0 1px rgba(255,255,255,0.10)",
                    }}
                  >
                    <IonIcon
                      icon={addOutline}
                    />
                  </IonFabButton>

                  <IonFabList
                    side="top"
                  >
                    <IonFabButton
                      onClick={handleSchedShiftReminderAllJobs}
                      color="medium"
                      style={{
                        "--border-radius": "15px",
                        "--box-shadow": "0 10px 26px rgba(0,0,0,0.35), 0 0 0 1px rgba(255,255,255,0.10)",
                      }}
                    >
                      <IonIcon
                        size="large"
                        icon={megaphoneOutline}
                      />
                    </IonFabButton>

                    <IonFabButton
                      onClick={handleCreateJob}
                      color="secondwarning"
                      style={{
                        "--border-radius": "15px",
                        "--box-shadow": "0 10px 26px rgba(0,0,0,0.35), 0 0 0 1px rgba(255,255,255,0.10)",
                      }}
                    >
                      <IonIcon
                        size="large"
                        icon={add}
                      />
                    </IonFabButton>
                  </IonFabList>
                </IonFab>
              )}
            </div>

            {/* Right Column - Job Detail Panel or Edit Form */}
            <div 
              style={{ 
                width: '50%',
                height: "100%", 
                borderLeft: "1px solid var(--ion-color-light-shade)",
                padding: 0,
                overflow: "hidden"
              }}
            >
              {scheduleJobPanelMode ? (
                <div style={{ height: "100%", overflow: "auto" }}>
                  <ScheduleJobDesktopWrapper 
                    mode={scheduleJobPanelMode}
                    jobDocId={editingJobId}
                    onComplete={handleScheduleJobComplete}
                  />
                </div>
              ) : selectWerkersJobId ? (
                <div style={{ height: "100%", overflow: "auto" }}>
                  <SelectWerkersDesktopWrapper
                    jobId={selectWerkersJobId}
                    onComplete={handleSelectWerkersComplete}
                  />
                </div>
              ) : selectWerkersSeriesState ? (
                <div style={{ height: "100%", overflow: "auto" }}>
                  <SelectWerkersSeriesDesktopWrapper
                    seriesId={selectWerkersSeriesState.seriesId}
                    jobIds={selectWerkersSeriesState.jobIds}
                    onComplete={handleSelectWerkersSeriesComplete}
                  />
                </div>
              ) : (
                <JobDetailPanel 
                  jobId={selectedJobId}
                  isScheduler={isScheduler}
                  onEditRequest={handleEditJob}
                  onCloneRequest={handleCloneJob}
                  onEditWerkersRequest={handleEditWerkersRequest}
                  onJobDeleted={handleJobDeleted}
                />
              )}
            </div>
        </div>
      )}
      </div>
    </WebLayout>
  );
});

// Wrapper to provide ScheduleJob with desktop mode props
const ScheduleJobDesktopWrapper: React.FC<{
  mode: "create" | "edit" | "clone";
  jobDocId: string | null;
  onComplete: (result?: {
    nextView?: "details" | "selectWerkers" | "selectWerkersSeries";
    jobId?: string;
    seriesId?: string;
    jobIds?: string[];
  }) => void;
}> = ({ 
  mode,
  jobDocId, 
  onComplete 
}) => {
  if ((mode === "edit" || mode === "clone") && !jobDocId) return null;
  
  return (
    <ScheduleJob 
      desktopMode={{
        isDesktopMode: true,
        mode,
        jobDocId: jobDocId || undefined,
        onComplete: onComplete,
      }}
    />
  );
};

const SelectWerkersDesktopWrapper: React.FC<{ jobId: string; onComplete: (result?: { nextView?: "details"; jobId?: string }) => void }> = ({
  jobId,
  onComplete,
}) => {
  return (
    <SelectWerkers
      desktopMode={{
        isDesktopMode: true,
        jobId,
        onComplete,
      }}
    />
  );
};

const SelectWerkersSeriesDesktopWrapper: React.FC<{
  seriesId: string;
  jobIds?: string[];
  onComplete: (result?: { nextView?: "details"; jobId?: string }) => void;
}> = ({ seriesId, jobIds, onComplete }) => {
  return (
    <SelectWerkersSeries
      desktopMode={{
        isDesktopMode: true,
        seriesId,
        jobIds,
        onComplete,
      }}
    />
  );
};

export default HomeDesktop;
